/**
 * Auth Context - Manages authentication state with Supabase
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Session } from '@supabase/supabase-js';
import {
  supabase,
  signInWithGoogle,
  signInWithEmail,
  signUpWithEmail,
  signOut,
} from '../lib/supabase';
import { API_CONFIG } from '../config';
import { useUserStore } from '../stores/user';
import { useGameStore } from '../stores/game';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const setCreditsRef = useRef(useUserStore.getState().setCredits);

  useEffect(() => {
    setCreditsRef.current = useUserStore.getState().setCredits;
  });

  const syncCredits = useCallback(async (accessToken: string) => {
    try {
      const res = await fetch(`${API_CONFIG.RATE_URL}/user/credits`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (res.ok) {
        const data = await res.json() as { credits: number };
        setCreditsRef.current(Math.max(0, data.credits));
      }
    } catch {
      // Silently fail — credits will be synced on next API call
    }
  }, []);

  useEffect(() => {
    // Timeout: if Supabase is unreachable, stop loading after 5s so the app still renders
    const loadingTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 5000);

    supabase.auth.getSession().then(({ data: { session } }) => {
      clearTimeout(loadingTimeout);
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);

      if (session?.access_token) {
        syncCredits(session.access_token);
        useGameStore.getState().loadFromServer(session.access_token);
      }
    }).catch(() => {
      clearTimeout(loadingTimeout);
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);

      if (session?.access_token) {
        syncCredits(session.access_token);
        useGameStore.getState().loadFromServer(session.access_token);
      }

      // Track auth + identify user for analytics
      if (event === 'SIGNED_IN' && session?.user) {
        import('../lib/analytics').then(({ identifyUser, trackLogin }) => {
          identifyUser(session.user.id, session.user.email || '');
          trackLogin(session.user.app_metadata?.provider === 'google' ? 'google' : 'email');
        });
      }

      // After Google OAuth redirect, honor any pending route (e.g. lazy registration → /upload)
      if (event === 'SIGNED_IN') {
        const postAuthRoute = sessionStorage.getItem('postAuthRoute');
        if (postAuthRoute) {
          sessionStorage.removeItem('postAuthRoute');
          navigate(postAuthRoute);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [syncCredits, navigate]);

  const handleSignInWithGoogle = async () => {
    try {
      await signInWithGoogle();
    } catch (error) {
      console.error('Google sign-in error:', error);
      throw error;
    }
  };

  const handleSignInWithEmail = async (email: string, password: string) => {
    await signInWithEmail(email, password);
  };

  const handleSignUpWithEmail = async (email: string, password: string) => {
    const result = await signUpWithEmail(email, password);
    if (result.user && !result.session) {
      throw new Error('__EMAIL_CONFIRMATION_REQUIRED__');
    }
    import('../lib/analytics').then(({ trackSignup }) => trackSignup('email'));
  };

  const handleSignOut = async () => {
    try {
      import('../lib/analytics').then(({ trackLogout }) => trackLogout());
      await signOut();
      setCreditsRef.current(0);
      useGameStore.getState().resetState();
    } catch (error) {
      console.error('Sign-out error:', error);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isAuthenticated: !!user,
    signInWithGoogle: handleSignInWithGoogle,
    signInWithEmail: handleSignInWithEmail,
    signUpWithEmail: handleSignUpWithEmail,
    signOut: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function useAuthToken(): string | null {
  const { session } = useAuth();
  return session?.access_token ?? null;
}
